import React, { useEffect, useState } from 'react';
import { Typography, Input, Button, Tag, Spin, Tooltip } from 'antd';
import { EyeOutlined, ReloadOutlined } from '@ant-design/icons';
import { inventoryService, InventoryItem } from '../../services/inventoryService';
import InventoryDetailModal from '../../components/inventory/InventoryDetailModal';
import styles from './InventoryPageMobile.module.css';

const { Title } = Typography;
const { Search } = Input;

const InventoryPageMobile: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [filteredInventory, setFilteredInventory] = useState<InventoryItem[]>([]);
  const [searchText, setSearchText] = useState('');
  const [selectedMedicineId, setSelectedMedicineId] = useState<number | null>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);

  useEffect(() => {
    loadInventory();
  }, []);

  useEffect(() => {
    filterInventory();
  }, [inventory, searchText]);

  const loadInventory = async () => {
    setLoading(true);
    try {
      const data = await inventoryService.getInventory();
      setInventory(data);
    } catch (error) {
      console.error('Error loading inventory:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterInventory = () => {
    if (!searchText.trim()) {
      setFilteredInventory(inventory);
      return;
    }
    const filtered = inventory.filter(item =>
      item.name.toLowerCase().includes(searchText.toLowerCase()) ||
      item.code.toLowerCase().includes(searchText.toLowerCase())
    );
    setFilteredInventory(filtered);
  };

  const handleViewDetails = (medicineId: number) => {
    setSelectedMedicineId(medicineId);
    setDetailModalVisible(true);
  };

  const getStockStatus = (item: InventoryItem) => {
    if (item.total_quantity === 0) {
      return <Tag color="red">Hết hàng</Tag>;
    }
    if (item.total_quantity <= item.min_stock_level) {
      return <Tag color="orange">Tồn kho thấp</Tag>;
    }
    return <Tag color="green">Ổn định</Tag>;
  };

  return (
    <div className={styles['inventory-mobile-container']}>
      <div className={styles['inventory-mobile-header']}>
        <div style={{ position: 'relative', marginBottom: 16 }}>
          <Input
            className={styles['inventory-mobile-search']}
            style={{ width: '100%', padding: '12px 40px 12px 16px', border: '1px solid #ced4da', borderRadius: 8, fontSize: 16, boxSizing: 'border-box' }}
            placeholder="Tìm kiếm thuốc..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
          />
          <span style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', color: '#6c757d', fontSize: 18 }}>
            <ReloadOutlined />
          </span>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <Button
            style={{ flexGrow: 1, padding: 12, fontSize: 16, fontWeight: 600, borderRadius: 8, border: '1px solid #ced4da', color: '#212529', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
            icon={<ReloadOutlined />}
            onClick={loadInventory}
            loading={loading}
          >
            Làm mới
          </Button>
        </div>
      </div>
      <Spin spinning={loading}>
        <div className={styles['inventory-mobile-list']}>
          {filteredInventory.map(item => (
            <div
              key={item.id}
              className={styles['inventory-mobile-card']}
              style={{
                maxWidth: 420,
                width: '100%',
                background: '#fff',
                borderRadius: 16,
                boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                border: '1px solid #e5e7eb',
                margin: '0 auto 4px',
                overflow: 'hidden',
              }}
            >
              <div style={{ padding: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontWeight: 700, fontSize: 18, color: '#1890ff', textAlign: 'left' }}>{item.name}</div>
                    <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4, textAlign: 'left' }}>Mã thuốc: {item.code}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 15, textAlign: 'left' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', textAlign: 'left' }}>
                    <span style={{ color: '#6b7280', textAlign: 'left' }}>Tồn kho:</span>
                    <span style={{ fontWeight: 500, color: '#222', textAlign: 'left' }}><b>{item.total_quantity}</b> {item.unit}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', textAlign: 'left' }}>
                    <span style={{ color: '#6b7280', textAlign: 'left' }}>Lô:</span>
                    <span style={{ fontWeight: 500, color: '#222', textAlign: 'left' }}>{item.lot_count}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', textAlign: 'left' }}>
                    <span style={{ color: '#6b7280', textAlign: 'left' }}>Mức tối thiểu:</span>
                    <span style={{ fontWeight: 500, color: '#222', textAlign: 'left' }}>{item.min_stock_level} {item.unit}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', textAlign: 'left' }}>
                    <span style={{ color: '#6b7280', textAlign: 'left' }}>Trạng thái:</span>
                    <span>{getStockStatus(item)}</span>
                  </div>
                </div>
              </div>
              <div style={{ background: '#f9fafb', padding: '12px 24px', borderTop: '1px solid #e5e7eb' }}>
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8 }}>
                  <button
                    type="button"
                    onClick={e => {
                      e.stopPropagation();
                      handleViewDetails(item.id);
                    }}
                    className={styles['inventory-mobile-detail-btn']}
                    style={{ padding: 8, borderRadius: '50%', color: '#1890ff', background: 'none', border: 'none', fontSize: 20, transition: 'all 0.2s' }}
                  >
                    <EyeOutlined />
                  </button>
                </div>
              </div>
            </div>
          ))}
          {filteredInventory.length === 0 && (
            <div className={styles['inventory-mobile-empty']}>Không có thuốc nào phù hợp.</div>
          )}
        </div>
      </Spin>
      <InventoryDetailModal
        visible={detailModalVisible}
        medicineId={selectedMedicineId}
        onClose={() => {
          setDetailModalVisible(false);
          setSelectedMedicineId(null);
        }}
      />
    </div>
  );
};

export default InventoryPageMobile;
